import { z } from '@hono/zod-openapi'

export const LocaleCodeSchema = z.string().min(2).max(10).openapi({
  example: 'en-US',
  description: 'BCP-47 locale code — branded type',
})

export const DirectionSchema = z.enum(['ltr', 'rtl']).openapi({
  example: 'ltr',
})

export const ScriptSchema = z.enum(['Latin', 'Arabic', 'CJK', 'Devanagari', 'Cyrillic', 'Hmong']).openapi({
  example: 'Latin',
})

export const PluralFormSchema = z.enum(['zero', 'one', 'two', 'few', 'many', 'other']).openapi({
  example: 'one',
})

export const NumberFormatSchema = z.object({
  decimal: z.string().length(1).openapi({ example: '.' }),
  group: z.string().length(1).openapi({ example: ',' }),
  currency: z.string().min(1).max(10).openapi({ example: 'USD' }),
  percent: z.string().min(1).max(3).openapi({ example: '%' }),
}).openapi('NumberFormat')

export const DateFormatSchema = z.object({
  short: z.string().openapi({ example: 'MM/DD/YYYY' }),
  long: z.string().openapi({ example: 'MMMM DD, YYYY' }),
  input: z.string().openapi({ example: 'YYYY-MM-DD' }),
}).openapi('DateFormat')

export const TimeFormatSchema = z.object({
  short: z.string().openapi({ example: 'h:mm A' }),
  long: z.string().openapi({ example: 'h:mm:ss A z' }),
}).openapi('TimeFormat')

export const LanguageSchema = z.object({
  code: LocaleCodeSchema,
  language: z.string().min(1).max(100).openapi({ example: 'Spanish' }),
  nativeName: z.string().min(1).max(100).openapi({ example: 'Español' }),
  direction: DirectionSchema,
  script: ScriptSchema,
  enabled: z.boolean().openapi({ example: true }),
  fallback: z.array(LocaleCodeSchema).openapi({ example: ['es'] }),
  pluralForms: z.array(PluralFormSchema).openapi({ example: ['one', 'other'] }),
  dateFormat: DateFormatSchema,
  timeFormat: TimeFormatSchema,
  numberFormat: NumberFormatSchema,
  fontFamily: z.string().optional().openapi({ example: 'Noto Sans, sans-serif' }),
  googleFontName: z.string().optional().openapi({ example: 'Noto+Sans' }),
  translationRequired: z.boolean().openapi({ example: true }),
  regulatory: z.array(z.string()).openapi({ example: ['Title VI', 'WA-RCW-28A.600'] }),
}).openapi('Language')

export const TerminologyEntrySchema = z.object({
  id: z.string().min(1).max(64).openapi({ example: 'term-iep' }),
  term: z.string().min(1).max(200).openapi({ example: 'Individualized Education Program' }),
  domain: z.enum(['sped', 's504', 'el', 'health', 'general', 'legal']).openapi({ example: 'sped' }),
  context: z.string().min(1).max(500).openapi({ example: 'A legal document outlining special education services' }),
  translations: z.record(LocaleCodeSchema, z.string()).openapi({ example: { 'es-US': 'Programa de Educación Individualizada' } }),
  approvedBy: z.string().min(1).openapi({ example: 'system' }),
  approvedAt: z.string().datetime().openapi({ example: '2026-07-01T00:00:00Z' }),
  status: z.enum(['approved', 'pending', 'needs-review']).openapi({ example: 'approved' }),
}).openapi('TerminologyEntry')

export const DistrictLocalizationConfigSchema = z.object({
  tenantId: z.string().min(1).max(64).openapi({ example: 'dist-wa-12345' }),
  defaultLocale: LocaleCodeSchema.openapi({ example: 'en-US' }),
  availableLocales: z.array(LocaleCodeSchema).openapi({ example: ['en-US', 'es-US', 'vi-VN'] }),
  mandatedLocales: z.array(LocaleCodeSchema).openapi({ example: ['es-US'] }),
  dateFormat: DateFormatSchema.partial().optional().openapi({ description: 'Override default date format' }),
  timeFormat: TimeFormatSchema.partial().optional().openapi({ description: 'Override default time format' }),
  numberFormat: NumberFormatSchema.partial().optional().openapi({ description: 'Override default number format' }),
  terminology: z.array(z.string()).openapi({ example: ['term-iep', 'term-fape'] }),
}).openapi('DistrictLocalizationConfig')

export type Language = z.infer<typeof LanguageSchema>
export type TerminologyEntry = z.infer<typeof TerminologyEntrySchema>
export type DistrictLocalizationConfig = z.infer<typeof DistrictLocalizationConfigSchema>
export type NumberFormat = z.infer<typeof NumberFormatSchema>
export type DateFormat = z.infer<typeof DateFormatSchema>
export type TimeFormat = z.infer<typeof TimeFormatSchema>
